import { useEffect, useLayoutEffect, useState, type ReactNode } from 'react'
import { ThemeContext } from './ThemeContext'
import type { Theme, ResolvedTheme, FontSize } from './ThemeContext'

const STORAGE_KEY = 'qpoint-theme'
const FONT_SIZE_KEY = 'qpoint-font-size'

const FONT_SIZE_SCALE: Record<FontSize, string> = {
  small: '87.5%',
  default: '100%',
  large: '112.5%',
  'extra-large': '125%',
}

function applyFontSize(size: FontSize) {
  document.documentElement.style.fontSize = FONT_SIZE_SCALE[size]
}

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light'
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light'
}

function applyTheme(resolved: ResolvedTheme) {
  const root = document.documentElement
  root.classList.toggle('dark', resolved === 'dark')
  root.style.colorScheme = resolved
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored === 'light' || stored === 'dark' || stored === 'system')
        return stored
    } catch (e) {
      console.error(e)
    }
    return 'system'
  })

  const [fontSize, setFontSizeState] = useState<FontSize>(() => {
    try {
      const stored = localStorage.getItem(FONT_SIZE_KEY)
      if (
        stored === 'small' ||
        stored === 'default' ||
        stored === 'large' ||
        stored === 'extra-large'
      )
        return stored
    } catch (e) {
      console.error(e)
    }
    return 'default'
  })

  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(getSystemTheme)

  const resolvedTheme: ResolvedTheme = theme === 'system' ? systemTheme : theme

  useLayoutEffect(() => {
    applyTheme(resolvedTheme)
  }, [resolvedTheme])

  useLayoutEffect(() => {
    applyFontSize(fontSize)
  }, [fontSize])

  useEffect(() => {
    if (theme !== 'system') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => {
      const next: ResolvedTheme = e.matches ? 'dark' : 'light'
      applyTheme(next)
      setSystemTheme(next)
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [theme])

  const setTheme = (next: Theme) => {
    setThemeState(next)
    try {
      localStorage.setItem(STORAGE_KEY, next)
    } catch (e) {
      console.error(e)
    }
  }

  const setFontSize = (next: FontSize) => {
    setFontSizeState(next)
    try {
      localStorage.setItem(FONT_SIZE_KEY, next)
    } catch (e) {
      console.error(e)
    }
  }

  return (
    <ThemeContext.Provider
      value={{ theme, resolvedTheme, setTheme, fontSize, setFontSize }}
    >
      {children}
    </ThemeContext.Provider>
  )
}
